/**
 * S59c / plan 2 §10.4 — suggest-analysis card.
 * Admins start a run (chat or report mode); members see a disabled CTA.
 * DOM text is set via text nodes only (el → createTextNode).
 */
import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { resolvedOf } from './shape.js';

/**
 * Chat group from the current URL; product comes from the group (not the URL).
 * Admin role from the session user. Callers may pass productId via actions once
 * the group's product is known; otherwise POST /analysis uses group.product_id.
 * @returns {{ groupId: string, productId: string, isAdmin: boolean }}
 */
export function suggestAnalysisContext() {
  const path = String((typeof window !== 'undefined' && window.location && window.location.pathname) || '');
  const match = path.match(/\/app\/chat\/([^/?#]+)/);
  const rawId = match && match[1] ? decodeURIComponent(match[1]) : '';
  const groupId = rawId && rawId !== 'new' ? rawId : '';
  let role = '';
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : '';
    const user = raw ? JSON.parse(raw) : null;
    role = user && user.role ? String(user.role) : '';
  } catch {
    role = '';
  }
  return { groupId, productId: '', isAdmin: role === 'admin' };
}

/**
 * @param {object} block
 * @param {{ groupId?: string, productId?: string, isAdmin?: boolean, apiPost?: Function }} [actions]
 * @returns {HTMLElement}
 */
export function renderSuggestAnalysis(block, actions) {
  const data = resolvedOf(block);
  const question = String(data.question == null ? '' : data.question).trim();
  const reason = String(data.reason == null ? '' : data.reason).trim();
  const ctx = suggestAnalysisContext();
  const groupId = (actions && actions.groupId) || ctx.groupId;
  const productId = (actions && actions.productId) || ctx.productId;
  const isAdmin = actions && typeof actions.isAdmin === 'boolean' ? actions.isAdmin : ctx.isAdmin;
  const post = (actions && typeof actions.apiPost === 'function')
    ? actions.apiPost
    : (path, body) => api(path, { method: 'POST', body });

  const status = el('p', { class: 'chat-suggest-status', hidden: true });
  const modeRow = el('div', {
    class: 'chat-followups chat-suggest-modes',
    role: 'group',
    'aria-label': 'Start deep analysis',
  });

  const root = el('div', {
    class: 'chat-block chat-block-suggest-analysis',
    'data-block-type': 'suggest-analysis',
    'data-block-id': (block && block.id) || '',
    'aria-label': 'Deep analysis suggestion',
  },
  el('p', { class: 'chat-block-title' }, 'Deep analysis suggested'),
  question ? el('p', { class: 'chat-suggest-question' }, question) : null,
  reason ? el('p', { class: 'chat-suggest-reason' }, reason) : null,
  modeRow,
  status);

  if (!isAdmin) {
    const disabled = el('button', {
      type: 'button',
      class: 'chat-chip chat-suggest-start',
      disabled: true,
    }, 'Start deep analysis');
    modeRow.append(
      disabled,
      el('p', { class: 'chat-suggest-note' }, 'Ask an admin to start this analysis')
    );
    return root;
  }

  const chatBtn = el('button', {
    type: 'button',
    class: 'chat-chip chat-suggest-mode',
    'data-mode': 'chat',
  }, 'In chat');
  const reportBtn = el('button', {
    type: 'button',
    class: 'chat-chip chat-suggest-mode',
    'data-mode': 'report',
  }, 'Full report');
  modeRow.append(
    el('p', { class: 'chat-suggest-cta' }, 'Start deep analysis'),
    chatBtn,
    reportBtn
  );

  let busy = false;

  async function start(mode, button) {
    if (busy) return;
    if (!groupId || !question) {
      setStatus('Missing chat or question.', true);
      return;
    }
    busy = true;
    chatBtn.disabled = true;
    reportBtn.disabled = true;
    if (button) button.textContent = 'Starting…';
    setStatus('', false);

    const body = {
      chat_group_id: groupId,
      question,
      mode: mode === 'report' ? 'report' : 'chat',
    };
    if (productId) body.product_id = productId;

    const res = await post('/analysis', body);
    if (res && !res.error && (res.run || res.queue_position != null)) {
      setStatus('Started', false);
      chatBtn.disabled = true;
      reportBtn.disabled = true;
      chatBtn.textContent = 'In chat';
      reportBtn.textContent = 'Full report';
      return;
    }
    busy = false;
    chatBtn.disabled = false;
    reportBtn.disabled = false;
    chatBtn.textContent = 'In chat';
    reportBtn.textContent = 'Full report';
    const statusCode = res && res.status;
    if (statusCode === 403 || statusCode === 404) {
      setStatus(statusCode === 403 ? 'Not allowed.' : 'Not available.', true);
      return;
    }
    setStatus((res && res.error) ? String(res.error) : 'Could not start.', true);
  }

  function setStatus(text, isError) {
    status.hidden = !text;
    status.textContent = text || '';
    status.setAttribute(
      'class',
      isError ? 'chat-suggest-status is-error' : 'chat-suggest-status'
    );
  }

  chatBtn.addEventListener('click', () => start('chat', chatBtn));
  reportBtn.addEventListener('click', () => start('report', reportBtn));

  return root;
}
